import get from 'lodash/get';
import has from 'lodash/has';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isUndefined from 'lodash/isUndefined';
import toString from 'lodash/toString';
import moment from 'moment';
import { CHECKBOX_COLUMN_TYPE, DEFAULT_COLUMN_PRECISION } from 'common/authoring_workflow/constants';
import { Expression } from '../types';
import { I18n } from 'common/visualizations';
import { ICellClassesParams, tableExportRenderer, unsupportedDateFormats } from './TableExportFormatting';
import { GROUP_COLUMN_PREFIX, NULL_GROUPING } from '../Constants';
import { SoQLType } from 'common/types/soql';
import { asParsableNumber, isConditionMet } from './TableColumnFormatting';
import { DECIMAL_PLACES_LIMIT } from './xlsxExportCellStylesHelper';

export const getConfigClassIndex = (params: ICellClassesParams, columnName: string) => {
  const { colDataTypeName, value, context } = params;
  const columnFormatItem = context.columnFormats[columnName];
  const conditionalConfig = columnFormatItem?.conditionalConfig;
  return conditionalConfig?.findIndex((config: { expression: Expression }) => {
    const expression = config.expression;
    const expressionFunction = get(expression, 'function', '');
    const isNumberType = isEqual(SoQLType.SoQLNumberT, colDataTypeName);
    const processedValue = isNumberType ? asParsableNumber(value, columnFormatItem) : value;
    const booleanValueMatched =
      includes([CHECKBOX_COLUMN_TYPE], colDataTypeName) && expressionFunction === processedValue?.toString();
    return booleanValueMatched || isConditionMet(expression, processedValue, isNumberType);
  });
};

export const isWithSubtotalLabel = (params: ICellClassesParams): boolean => {
  const { value, context, colDef, node } = params;
  if (!isUndefined(colDef) && node.footer) {
    const rendererParams = {
      value: value,
      context: context,
      colDef: colDef,
      data: node?.data,
      node: node,
      forExport: true
    };
    const renderer = tableExportRenderer(colDef, rendererParams);
    const formattedValue = typeof renderer === 'string' ? renderer : renderer.props.children;
    return formattedValue.includes(I18n.t('shared.visualizations.charts.table.subtotal'));
  }
  return false;
};

export const shouldApplyDateFormattingClass = (params: ICellClassesParams, columnName: string): boolean => {
  const { context, value, colDataTypeName } = params;
  const isCalendarType = isEqual(SoQLType.SoQLFloatingTimestampT, colDataTypeName);
  const isValidDate = moment(value, moment.ISO_8601, true).isValid();
  const isEmptyOrNullGrouping = isEmpty(value) || isEqual(value, NULL_GROUPING);
  const applyDateFormattingClass =
    isCalendarType && isValidDate && !isEmptyOrNullGrouping && !isWithSubtotalLabel(params);
  const dateViewFormat = context.columnFormats[columnName]?.format?.view as string;
  return applyDateFormattingClass && !unsupportedDateFormats.includes(dateViewFormat);
};

export const getNumberPrecisionInfo = (params: ICellClassesParams) => {
  const { colFieldName, context, value, node } = params;
  const columnName = colFieldName.replace(`${GROUP_COLUMN_PREFIX}-`, '');
  const columnFormatItem = context.columnFormats[columnName];
  const columnViewFormatItem = context.columnMetadata.find((column) => column.fieldName == colFieldName);
  const valueStr = toString(value);
  const decimalLength = valueStr.includes('.') ? valueStr.split('.')[1].length : 0;
  const referenceFormat = isUndefined(columnFormatItem?.format)
    ? columnViewFormatItem?.format
    : columnFormatItem?.format;
  const precision = referenceFormat?.precision;
  const hasPrecisionKey = has(referenceFormat, 'precision') && !isUndefined(referenceFormat?.precision);
  let decimalPlaces = hasPrecisionKey ? precision : decimalLength;
  if (parseInt(toString(decimalPlaces)) > DECIMAL_PLACES_LIMIT && node.footer && isUndefined(precision)) {
    decimalPlaces = DEFAULT_COLUMN_PRECISION;
  }
  const isPercentage = referenceFormat?.precisionStyle != 'percentage';
  const isDecimal =
    !!(hasPrecisionKey && decimalPlaces) || !!(!hasPrecisionKey && decimalLength && isPercentage);
  return { isDecimal, decimalPlaces };
};
